import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import type { TanStackRouteSearch } from "@/types/url-params.types";

/**
 * Context value for the Analysis route. URL is the source of truth.
 * Provided by AnalysisRoute; consumers read params and update URL via {@link navigateWithSearch}.
 * Analysis is the new name for News Feed.
 */
export interface AnalysisParamsContextValue {
  feedId: string;
  /** Start date YYYY-MM-DD */
  startDate: string;
  /** End date YYYY-MM-DD */
  endDate: string;
  /** Number of days for preset range (e.g. 7, 30); from URL. */
  filterDays: TanStackRouteSearch["filterDays"];
  /** Chart aggregation period (day, week, month, etc.); from URL. */
  period: TanStackRouteSearch["period"];
  /** True when URL has custom startDate/endDate instead of filterDays preset. */
  isCustomDaysRange: boolean;
  /** Selected filters by category (matches TanStack search `selected`) */
  selectedFilters: Partial<Record<ShareVoiceSidebarCategory, string[]>>;
  /** Key message AND/OR from URL search `keyMessageConjunction`. */
  keyMessageConjunction?: TanStackRouteSearch["keyMessageConjunction"];
  /** Height in px of the sticky header block (date panel + control panel) for content sticky offset. */
  stickyHeaderHeight?: number;
  /** Merge updates into current URL search and navigate. */
  navigateWithSearch: (updates: Partial<TanStackRouteSearch>) => void;
}

const AnalysisParamsContext = createContext<AnalysisParamsContextValue | null>(
  null,
);

export interface AnalysisParamsProviderProps {
  value: AnalysisParamsContextValue;
  children: ReactNode;
}

/**
 * Provider for Analysis route params. Wrap Analysis route content.
 * AnalysisRoute computes the value from TanStack route params/search and passes it here.
 */
export const AnalysisParamsProvider = ({
  value,
  children,
}: AnalysisParamsProviderProps) => {
  return (
    <AnalysisParamsContext.Provider value={value}>
      {children}
    </AnalysisParamsContext.Provider>
  );
};

/**
 * Hook to read Analysis params from context.
 * Returns null when not inside an {@link AnalysisParamsProvider}.
 */
export const useAnalysisParamsContext =
  (): AnalysisParamsContextValue | null => {
    return useContext(AnalysisParamsContext);
  };

/**
 * Hook that requires the Analysis params context. Throws if used outside {@link AnalysisParamsProvider}.
 */
const useAnalysisParamsContextRequired = (): AnalysisParamsContextValue => {
  const value = useContext(AnalysisParamsContext);
  if (!value) {
    throw new Error(
      "useAnalysisParamsContextRequired must be used within an AnalysisParamsProvider",
    );
  }
  return value;
};

// --- Sidebar filter params (feed sidebar category filters) ---

/** Sidebar category filter state (TIER, PROMINENCE, etc.); see {@link AnalysisSidebarFilterParamsProvider}. */
export interface AnalysisSidebarFilterParamsContextValue {
  selectedFilters: Map<ShareVoiceSidebarCategory, string[]>;
  toggleFilterSelection: (
    category: ShareVoiceSidebarCategory,
    item: string | number,
  ) => void;
  /** Set full list of selected ids for a category. Only on Analysis (URL); old news feed uses toggle in a loop. */
  setCategorySelection?: (
    category: ShareVoiceSidebarCategory,
    ids: string[],
  ) => void;
}

const AnalysisSidebarFilterParamsContext =
  createContext<AnalysisSidebarFilterParamsContextValue | null>(null);

export { AnalysisSidebarFilterParamsContext };

/**
 * Hook for the feed filter sidebar params. Must be used within a SidebarFilterParams provider.
 */
export const useAnalysisSidebarFilterParams =
  (): AnalysisSidebarFilterParamsContextValue => {
    const value = useContext(AnalysisSidebarFilterParamsContext);
    if (!value) {
      throw new Error(
        "useAnalysisSidebarFilterParams must be used within an AnalysisSidebarFilterParamsProvider (or a compatible AnalysisSidebarFilterParamsContext.Provider)",
      );
    }
    return value;
  };

/**
 * Provides sidebar filter params from Analysis params context (URL only, no React Router).
 * Use when rendering the filter sidebar on the Analysis route.
 */
export const AnalysisSidebarFilterParamsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const analysis = useAnalysisParamsContextRequired();

  const selectedFilters = useMemo((): Map<
    ShareVoiceSidebarCategory,
    string[]
  > => {
    const map = new Map<ShareVoiceSidebarCategory, string[]>();
    for (const [cat, ids] of Object.entries(analysis.selectedFilters)) {
      map.set(cat as ShareVoiceSidebarCategory, ids);
    }
    return map;
  }, [analysis.selectedFilters]);

  const toggleFilterSelection = useCallback(
    (category: ShareVoiceSidebarCategory, item: string | number) => {
      const itemStr = String(item);
      const current = analysis.selectedFilters[category] ?? [];
      const next = current.includes(itemStr)
        ? current.filter((id) => id !== itemStr)
        : [...current, itemStr];
      const newSelected = { ...analysis.selectedFilters };
      if (next.length === 0) {
        delete newSelected[category];
      } else {
        newSelected[category] = next;
      }
      analysis.navigateWithSearch({ selected: newSelected });
    },
    [analysis],
  );

  const setCategorySelection = useCallback(
    (category: ShareVoiceSidebarCategory, ids: string[]) => {
      const newSelected = { ...analysis.selectedFilters };
      if (ids.length === 0) {
        delete newSelected[category];
      } else {
        newSelected[category] = ids;
      }
      analysis.navigateWithSearch({ selected: newSelected });
    },
    [analysis],
  );

  const value = useMemo<AnalysisSidebarFilterParamsContextValue>(
    () => ({ selectedFilters, toggleFilterSelection, setCategorySelection }),
    [selectedFilters, toggleFilterSelection, setCategorySelection],
  );

  return (
    <AnalysisSidebarFilterParamsContext.Provider value={value}>
      {children}
    </AnalysisSidebarFilterParamsContext.Provider>
  );
};
